import { Fragment } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import OrderItemDropdown from './OrderItemDropdown';
import Accordion from '../../../components/Accordion';
import { SeasonsWithTransactions } from '../../../types/drupal';
import { Transaction } from '../../../types/customer';
import Link from '../../../components/Link';
import { getColorVars } from '../../../utils/functions';
import { ReactComponent as ExternalLinkIcon } from './outside-link-icon.svg';

interface Props {
  season: SeasonsWithTransactions;
}

function TransactionField(props: {
  label: string;
  text: string;
  className?: string;
}): JSX.Element {
  return (
    <div className={`my-2 basis-[50%] md:basis-[15%] ${props.className}`}>
      <p className='font-light'>{props.label}</p>
      <p className='font-[500]'>{props.text}</p>
    </div>
  );
}

function TransactionTitleField(props: {
  label: string;
  text: string;
  className?: string;
}): JSX.Element {
  return (
    <div className={`${props.className}`}>
      <p className='font-light'>{props.label}</p>
      <p className='font-[500]'>{props.text}</p>
    </div>
  );
}

function OrderItem(props: Props): JSX.Element {
  const { t } = useTranslation('order_history');
  const { textColor } = getColorVars();

  function getVatAmount(vat: number, price: number) {
    return ((vat * price) / (100 + vat)).toFixed(2);
  }

  function ITicketOrder(props: { transaction: Transaction }) {
    const { transaction } = props;

    const totalPrice = transaction.products.reduce((total, product) => {
      const price = product.priceInfo.grossListPrice ?? 0;
      return total + price;
    }, 0);

    const product = transaction.products.find(
      (product) => product?.priceInfo.vatPercentage,
    );

    return (
      <OrderItemDropdown
        transaction={transaction}
        title={
          transaction.resort.toLowerCase() === 'ruka'
            ? `Ruka ${t('Lift ticket webshop')}`
            : `Pyhä ${t('Lift ticket webshop')}`
        }
        titleElement={
          <div className='flex flex-wrap my-4 gap-4'>
            <TransactionTitleField
              label={t('Order date')}
              text={dayjs(transaction.dateTime).format('DD.MM.YYYY')}
            />

            <TransactionTitleField
              label={t('Booking code')}
              text={transaction.id}
            />

            {transaction.voucher && (
              <a
                href={transaction.voucher}
                className='border-1 border-black text-black py-0 px-2 no-underline rounded-md text-sm flex items-center h-auto uppercase'
              >
                {t('QR-Code')}
              </a>
            )}
          </div>
        }
      >
        <div className='accordionBottom flex justify-start flex-wrap'>
          {transaction.products &&
            transaction.products[0] &&
            transaction.products[0].bookingUrl && (
              <Link
                href={transaction.products[0].bookingUrl}
                external={true}
                className='block w-full my-4'
              >
                <span className='flex items-center gap-2'>
                  {t('Booking information') ?? 'Booking information'}
                  <ExternalLinkIcon className={`${textColor} h-4`} />
                </span>
              </Link>
            )}

          <TransactionField label={t('Total')} text={totalPrice + ' €'} />

          {!!product && (
            <TransactionField
              label={t('VAT')}
              text={
                (product.priceInfo.vatPercentage * 100).toFixed(2).toString() +
                ' %'
              }
            />
          )}
        </div>
      </OrderItemDropdown>
    );
  }

  function RentleOrder(props: { transaction: Transaction }) {
    const { transaction } = props;

    return (
      <OrderItemDropdown
        transaction={transaction}
        title={
          transaction.resort.toLowerCase() === 'ruka'
            ? 'Ruka Shop'
            : 'Pyhä Shop'
        }
        titleElement={
          <div className='flex my-4 gap-6'>
            <TransactionTitleField
              label={t('Order id')}
              text={transaction.id}
            ></TransactionTitleField>

            <TransactionTitleField
              label={t('Start date')}
              text={dayjs(transaction.dateTime).format('DD.MM.YYYY')}
            ></TransactionTitleField>
          </div>
        }
      >
        <>
          <div className='flex flex-wrap my-4 gap-2 justify-between'>
            <TransactionField
              label={t('Price')}
              text={transaction.price?.toString() + ' €'}
            ></TransactionField>

            <TransactionField
              label={t('Order date')}
              text={dayjs(transaction.dateTime).format('DD.MM.YYYY')}
            ></TransactionField>

            <TransactionField
              label={t('Resort')}
              text={transaction.resort}
            ></TransactionField>
          </div>
        </>
      </OrderItemDropdown>
    );
  }

  function TeksoOrderTicket(props: { transaction: Transaction }) {
    const { transaction } = props;
    const totalPrice = transaction.products.reduce((total, product) => {
      const price = product.priceInfo.totalPrice ?? 0;
      return total + price;
    }, 0);

    const shopName =
      transaction.shop ??
      (transaction.resort.toLowerCase() === 'ruka' ? 'Ruka Shop' : 'Pyhä Shop');

    return (
      <OrderItemDropdown
        transaction={transaction}
        title={shopName}
        titleElement={
          <div className='flex flex-wrap my-4 gap-4'>
            <TransactionTitleField
              label={t('Order date')}
              text={dayjs(transaction.dateTime).format('DD.MM.YYYY')}
            />

            <TransactionTitleField
              label={t('Receipt no.')}
              text={transaction.id}
            />

            <TransactionTitleField
              label={t('Total')}
              text={totalPrice.toString() + ' €'}
            />
          </div>
        }
      >
        <>
          {transaction.products.map((product, i) => {
            const vatAmount = getVatAmount(
              product.priceInfo.vatPercentage,
              product.priceInfo.totalPrice,
            );

            return (
              <div className='flex justify-start flex-wrap' key={i}>
                <p className='font-[500] w-full mb-4'>{product.productName}</p>

                <TransactionField
                  label={t('Price')}
                  text={product.priceInfo.totalPrice.toString() + ' €'}
                />

                <TransactionField
                  label={t('VAT')}
                  text={product.priceInfo.vatPercentage.toString() + ' %'}
                />

                <TransactionField
                  label={t('VAT amount')}
                  text={vatAmount + ' €'}
                />

                {i + 1 < transaction.products.length && (
                  <hr className='border-gray-300 my-6 border-1 w-full' />
                )}
              </div>
            );
          })}
        </>
      </OrderItemDropdown>
    );
  }

  function TeksoOrderSale(props: { transaction: Transaction }) {
    const { transaction } = props;

    const shopName =
      transaction.shop ??
      (transaction.resort.toLowerCase() === 'ruka' ? 'Ruka Shop' : 'Pyhä Shop');

    const totalPrice = transaction.products.reduce(
      (sum, product) => sum + product.priceInfo.totalPrice,
      0,
    );

    return (
      <OrderItemDropdown
        transaction={transaction}
        title={shopName}
        titleElement={
          <div className='flex my-4 gap-4'>
            {transaction.dateTime && (
              <TransactionTitleField
                label={t('Purchase date')}
                text={dayjs(transaction.dateTime).format('DD.MM.YYYY')}
              />
            )}
            <TransactionTitleField
              label={t('Receipt no.')}
              text={transaction.id}
            />

            <TransactionTitleField
              label={t('Total')}
              text={totalPrice + ' €'}
            />
          </div>
        }
      >
        <div>
          {transaction.products.map((product, i) => {
            const vatAmount = product.priceInfo.vatPercentage
              ? getVatAmount(
                  product.priceInfo.vatPercentage,
                  product.priceInfo.totalPrice,
                )
              : null;

            return (
              <Fragment key={i}>
                <div className='flex justify-start my-4 flex-wrap'>
                  <TransactionField
                    label={t('Product')}
                    text={product.productName.toLowerCase()}
                    className='basis-full capitalize'
                  />

                  <TransactionField
                    label={t('Quantity')}
                    text={product.quantity.toString()}
                  />

                  {product.size && (
                    <TransactionField label={t('Size')} text={product.size} />
                  )}

                  <TransactionField
                    label={t('Price')}
                    text={product.priceInfo.totalPrice.toString() + ' €'}
                  />

                  {product.priceInfo.discountAmount &&
                    product.priceInfo.discountAmount > 0 && (
                      <TransactionField
                        label={t('Discount')}
                        text={
                          product.priceInfo.discountAmount.toString() + ' €'
                        }
                      />
                    )}

                  <TransactionField
                    label={t('VAT')}
                    text={product.priceInfo.vatPercentage.toString() + ' %'}
                  />

                  {vatAmount && (
                    <TransactionField
                      label={t('VAT amount')}
                      text={vatAmount + ' €'}
                    />
                  )}
                </div>

                {i + 1 < transaction.products.length && (
                  <hr className='border-gray-300 my-6 border-1 w-full' />
                )}
              </Fragment>
            );
          })}
        </div>
      </OrderItemDropdown>
    );
  }

  return (
    <Accordion title={props.season.name}>
      <>
        {props.season.transactions &&
          props.season.transactions.map((transaction, i) => {
            if (
              transaction.source === 'iTicket' &&
              transaction.products &&
              transaction.products.length > 0
            ) {
              return <ITicketOrder key={i} transaction={transaction} />;
            } else if (transaction.source === 'Rentle') {
              return <RentleOrder key={i} transaction={transaction} />;
            } else if (
              transaction.source === 'Tekso' &&
              transaction.type === 'Sale' &&
              transaction.products &&
              transaction.products.length > 0
            ) {
              return <TeksoOrderSale key={i} transaction={transaction} />;
            } else if (
              transaction.source === 'Tekso' &&
              transaction.type === 'Ticket' &&
              transaction.products &&
              transaction.products.length > 0
            ) {
              return <TeksoOrderTicket key={i} transaction={transaction} />;
            }
          })}
      </>
    </Accordion>
  );
}

export default OrderItem;
